import React from 'react';
import {Pressable, StyleSheet, View, type StyleProp, type ViewStyle} from 'react-native';
import {useTranslation} from 'react-i18next';

import {Icon} from '@/shared/ui/icons';
import {AppText} from '@/shared/ui/text';
import {controls, radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Подвал таблицы — shadcn `DataTablePagination`, одной строкой:
 *
 *      1–20 из 134  · счётчики списка          Строк: [20|50|100]   ‹  3 / 7  ›
 *
 * Слева — диапазон и то, что список хочет сказать о выборке (`children`): сколько
 * онлайн, у скольких нет лица. Справа — размер страницы и листалка, сгруппированные
 * в две обводки, как `ToggleGroup` и `Pagination` у shadcn.
 *
 * Три правила, унаследованные от прежней рейки:
 *
 *   1. Диапазон печатается ВСЕГДА, пока в списке есть хоть одна строка: «1–3 из 3»
 *      говорит и что это всё, и сколько это.
 *   2. Размер страницы — только когда строк больше самого мелкого размера. Выбор «по
 *      20 / 50 / 100» под тремя строками не меняет ничего.
 *   3. Листалка — только когда страниц больше одной. «1 / 1» между двух погашенных
 *      стрелок — это три мёртвых контрола.
 *
 * Пустой список рейку не рисует вовсе.
 */
const styles = StyleSheet.create({
  root: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing(3),
    minWidth: 0,
    minHeight: controls.sm,
  },
  left: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing(3),
    flex: 1,
    minWidth: 160,
  },
  right: {flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: spacing(4)},
  group: {flexDirection: 'row', alignItems: 'center', gap: spacing(2)},
  /* Одна обводка на группу; кнопки внутри делят рамку, как в `ToggleGroup`. */
  joined: {
    flexDirection: 'row',
    alignItems: 'stretch',
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'hidden',
  },
  cell: {
    minWidth: controls.sm,
    height: controls.sm - 1,
    paddingHorizontal: spacing(2) + 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cellRule: {borderLeftWidth: StyleSheet.hairlineWidth},
  sizeLabel: {fontSize: 13},
  position: {minWidth: 52, textAlign: 'center'},
});

/** True, пока указатель над кнопкой или палец на ней. */
function isHot(state: {pressed: boolean}): boolean {
  return state.pressed || (state as {hovered?: boolean}).hovered === true;
}

function Step({
  direction,
  disabled,
  onPress,
  label,
  rule,
  testID,
}: {
  direction: 'prev' | 'next';
  disabled: boolean;
  onPress: () => void;
  label: string;
  rule?: boolean;
  testID?: string;
}) {
  const p = useTheme();
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{disabled}}
      disabled={disabled}
      onPress={onPress}
      hitSlop={4}
      style={state => [
        styles.cell,
        rule && [styles.cellRule, {borderLeftColor: p.input}],
        {
          backgroundColor: !disabled && isHot(state) ? p.accent : p.card,
          opacity: disabled ? 0.4 : 1,
        },
      ]}>
      <Icon name={direction === 'prev' ? 'chevronLeft' : 'chevron'} size={14} color={p.foreground} />
    </Pressable>
  );
}

export function Pager({
  page,
  totalPages,
  total,
  perPage,
  onPage,
  /** Размеры страницы на выбор. Опущены — селектор не рисуется. */
  perPageOptions,
  onPerPage,
  /** Счётчики выборки — рядом с диапазоном, слева. */
  children,
  style,
  testID = 'pager',
}: {
  page: number;
  totalPages: number;
  total: number;
  perPage: number;
  onPage: (page: number) => void;
  perPageOptions?: readonly number[];
  onPerPage?: (perPage: number) => void;
  children?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const {t} = useTranslation();
  const p = useTheme();

  if (total === 0) {
    return null;
  }

  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);

  // Листалка — только когда есть куда листать.
  const canPage = totalPages > 1;
  // Размер страницы — только когда строк больше самого мелкого из предложенных.
  const canResize =
    !!perPageOptions && !!onPerPage && total > Math.min(...perPageOptions);

  return (
    <View style={[styles.root, style]} testID={testID}>
      <View style={styles.left}>
        <AppText variant="caption" testID={`${testID}-range`}>
          {t('pager.range', {from, to, total})}
        </AppText>
        {children}
      </View>

      {canResize || canPage ? (
        <View style={styles.right}>
          {canResize && perPageOptions && onPerPage ? (
            <View style={styles.group}>
              <AppText variant="caption">{t('pager.perPage')}</AppText>
              <View style={[styles.joined, {borderColor: p.input}]}>
                {perPageOptions.map((size, i) => {
                  const active = size === perPage;
                  return (
                    <Pressable
                      key={size}
                      testID={`${testID}-size-${size}`}
                      accessibilityRole="button"
                      accessibilityState={{selected: active}}
                      accessibilityLabel={t('pager.perPageOption', {count: size})}
                      onPress={() => {
                        onPerPage(size);
                        onPage(1);
                      }}
                      hitSlop={4}
                      style={state => [
                        styles.cell,
                        i > 0 && [styles.cellRule, {borderLeftColor: p.input}],
                        {
                          backgroundColor: active
                            ? p.accent
                            : isHot(state)
                            ? p.muted
                            : p.card,
                        },
                      ]}>
                      <AppText
                        variant={active ? 'label' : 'body'}
                        color={active ? p.accentForeground : p.mutedForeground}
                        style={styles.sizeLabel}>
                        {String(size)}
                      </AppText>
                    </Pressable>
                  );
                })}
              </View>
            </View>
          ) : null}

          {canPage ? (
            <View style={[styles.joined, {borderColor: p.input}]}>
              <Step
                direction="prev"
                disabled={page <= 1}
                onPress={() => onPage(page - 1)}
                label={t('pager.prev')}
                testID={`${testID}-prev`}
              />
              <View
                style={[
                  styles.cell,
                  styles.cellRule,
                  {borderLeftColor: p.input, backgroundColor: p.card},
                ]}>
                <AppText
                  variant="caption"
                  color={p.foreground}
                  style={styles.position}
                  testID={`${testID}-position`}>
                  {page} / {totalPages}
                </AppText>
              </View>
              <Step
                direction="next"
                rule
                disabled={page >= totalPages}
                onPress={() => onPage(page + 1)}
                label={t('pager.next')}
                testID={`${testID}-next`}
              />
            </View>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}
